// FILE: src/pages/AdminUsersListPage.jsx
// Students (plan WP-8 / WP-7; matrix 9.8, 10.2; R2): search by name or
// email, filter by status, page through the server's results, see each
// student's progress and statuses as icon + words, and open the student's
// own page (B-21). Students only: staff accounts are under Staff & roles
// (RBAC). The search, filters and page live in the URL
// (?q=&status=&page=&limit=) so they survive reload and Back, and stay
// mounted while the list reloads, so keyboard focus is never lost.
// GET /api/admin/users?role=student → { users, totalCount, page, limit, totalPages }.
// Add student needs students.invite and Edit students.edit.
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { fetchUsers } from "../features/adminUsersSlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, clampPct } from "../components/ui";
import { ResponsiveTable } from "../components/admin/AdminBits";
import { ServerPagination } from "../components/admin/AuditLogTable";
import { PhoneCard, PhoneList, useIsPhone } from "../components/admin/PhoneList";
import { STATUS_FILTERS, StudentStatusChips } from "../components/admin/studentStatus";
import { formatDate, formatNumber, personName } from "../components/admin/format";
import { userStatusLabel } from "../i18n/labels";
import usePermissions from "../hooks/usePermissions";

const LIMITS = [25, 50, 100];

function positiveInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function enrolmentEnd(user) {
  if (user.role === "admin") return "—";
  if (user.enrolmentExpiresAt) return formatDate(user.enrolmentExpiresAt);
  if (user.invited) return "Starts when they register";
  return "—";
}

/** "3 of 9 chapters" with a labelled bar, from the server's counts. */
function ChapterProgress({ user, name }) {
  const c = user.chapters;
  if (user.role === "admin" || !c || !Number.isFinite(Number(c.total))) {
    return <Typography sx={{ color: "text.secondary" }}>—</Typography>;
  }
  const pct = c.total ? clampPct((c.completed / c.total) * 100) : 0;
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography sx={{ whiteSpace: "nowrap" }}>
        {formatNumber(c.completed)} of {formatNumber(c.total)} chapters
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        aria-label={`Chapters completed by ${name}`}
        aria-valuetext={`${c.completed} of ${c.total} chapters`}
        sx={{ mt: 0.75 }}
      />
    </Box>
  );
}

export default function AdminUsersListPage() {
  const dispatch = useDispatch();
  const { list, totalCount, totalPages, loading, error } = useSelector((state) => state.adminUsers);
  const [params, setParams] = useSearchParams();

  const statusParam = params.get("status");
  const status = STATUS_FILTERS.includes(statusParam) ? statusParam : "all";
  const { can } = usePermissions();
  const canInvite = can("students.invite");
  const canEdit = can("students.edit");
  const committed = params.get("q") || "";
  const page = positiveInt(params.get("page"), 1);
  const limitParam = positiveInt(params.get("limit"), LIMITS[0]);
  const limit = LIMITS.includes(limitParam) ? limitParam : LIMITS[0];
  const [searchTerm, setSearchTerm] = useState(committed);
  const phone = useIsPhone();

  // Back/Forward changes the URL: show the search that is in it.
  useEffect(() => setSearchTerm(committed), [committed]);

  const load = useCallback(() => {
    dispatch(
      fetchUsers({
        search: committed.trim() || undefined,
        status: status !== "all" ? status : undefined,
        role: "student",
        page,
        limit
      })
    );
  }, [dispatch, committed, status, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  /** Changes to the URL; any filter change goes back to page 1. */
  const updateParams = (next, { keepPage = false } = {}) => {
    const merged = { q: committed, status, page: keepPage ? page : 1, limit, ...next };
    const clean = {};
    if (merged.q && merged.q.trim()) clean.q = merged.q.trim();
    if (merged.status && merged.status !== "all") clean.status = merged.status;
    if (merged.page > 1) clean.page = String(merged.page);
    if (merged.limit !== LIMITS[0]) clean.limit = String(merged.limit);
    setParams(clean, { replace: !keepPage });
  };

  const users = Array.isArray(list) ? list : [];
  const filtered = committed.trim() !== "" || status !== "all";
  const addButton = (props = {}) =>
    canInvite ? (
      <Button component={RouterLink} to="/admin/users/add" variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} {...props}>
        Add student
      </Button>
    ) : null;
  const editButton = (user, name) =>
    canEdit ? (
      <Button component={RouterLink} to={`/admin/users/${user._id}/edit`} variant="outlined" startIcon={<EditOutlinedIcon />} aria-label={`Edit ${name}`}>
        Edit
      </Button>
    ) : null;
  const firstLoad = loading && users.length === 0;
  const lastPage = Math.max(1, Number(totalPages) || Math.ceil((totalCount || 0) / limit) || 1);

  return (
    <Box>
      <PageHeader
        title="Students"
        description="Find a student, see where they are in the course and open their page for progress, activity, records and account actions."
        actions={addButton()}
      />

      <Section id="student-list" title="All students" contentSx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "grid", gap: 2, mb: 2.5, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 240px" } }}>
          <Box
            component="form"
            role="search"
            aria-label="Students"
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ q: searchTerm });
            }}
            sx={{ display: "flex", gap: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" } }}
          >
            <TextField
              id="student-search"
              label="Search by name or email"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon aria-hidden />
                    </InputAdornment>
                  )
                }
              }}
            />
            <Button type="submit" variant="outlined" sx={{ flexShrink: 0, minHeight: 52 }}>
              Search
            </Button>
          </Box>
          <TextField
            select
            id="student-status"
            label="Status"
            value={status}
            onChange={(e) => updateParams({ status: e.target.value })}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s} value={s}>
                {s === "all" ? "Any status" : userStatusLabel(s)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {filtered && (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 2, flexWrap: "wrap", rowGap: 1 }}>
            <Typography role="status" sx={{ color: "text.secondary" }}>
              {loading ? "Searching…" : `${formatNumber(totalCount)} ${totalCount === 1 ? "student matches" : "students match"}`}
              {committed ? ` “${committed}”` : ""}
              {status !== "all" ? ` · ${userStatusLabel(status)}` : ""}
            </Typography>
            <Button
              startIcon={<FilterAltOffOutlinedIcon />}
              onClick={() => {
                setSearchTerm("");
                setParams({}, { replace: true });
              }}
            >
              Clear search and filters
            </Button>
          </Stack>
        )}

        {error && users.length === 0 ? (
          <ErrorState error={error} title="We couldn't load the students" onRetry={load} />
        ) : firstLoad ? (
          <LoadingState variant="table" rows={5} label="Loading students…" />
        ) : users.length === 0 ? (
          filtered ? (
            <EmptyState
              title="No students match"
              description="Check the spelling, search for part of the name or email, or choose another status."
            />
          ) : (
            <EmptyState
              title="No students yet"
              description={canInvite ? "Add a student and we'll email them an invitation to register." : "Students appear here once they're invited."}
              action={addButton()}
            />
          )
        ) : (
          <>
            {error && <ErrorState compact error={error} onRetry={load} />}
            <Box sx={{ height: 8, mb: 1 }}>
              {loading && <LinearProgress aria-label="Updating the student list" />}
            </Box>
            {phone ? (
              <PhoneList label="Students">
                {users.map((user) => {
                  const name = personName(user, "Name not given");
                  return (
                    <PhoneCard key={user._id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Link component={RouterLink} to={`/admin/users/${user._id}`} sx={{ fontWeight: 650 }}>
                          {name}
                        </Link>
                        <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{user.email || "—"}</Typography>
                        {user.registrationNumber && (
                          <Typography sx={{ color: "text.secondary" }}>Reg. {user.registrationNumber}</Typography>
                        )}
                      </Box>
                      <ChapterProgress user={user} name={name} />
                      <StudentStatusChips user={user} />
                      <Typography>
                        <Box component="span" sx={{ color: "text.secondary" }}>
                          Enrolment ends:{" "}
                        </Box>
                        {enrolmentEnd(user)}
                      </Typography>
                      {canEdit && <Box>{editButton(user, name)}</Box>}
                    </PhoneCard>
                  );
                })}
              </PhoneList>
            ) : (
              <ResponsiveTable label="Students" minWidth={980}>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Name
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Progress
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Status
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Enrolment ends
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const name = personName(user, "Name not given");
                    return (
                      <TableRow key={user._id} hover>
                        <TableCell component="th" scope="row" sx={{ maxWidth: 280 }}>
                          <Link component={RouterLink} to={`/admin/users/${user._id}`} sx={{ fontWeight: 600 }}>
                            {name}
                          </Link>
                          <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{user.email || "—"}</Typography>
                          {user.registrationNumber && (
                            <Typography sx={{ color: "text.secondary" }}>Reg. {user.registrationNumber}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <ChapterProgress user={user} name={name} />
                        </TableCell>
                        <TableCell>
                          <StudentStatusChips user={user} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{enrolmentEnd(user)}</TableCell>
                        <TableCell align="right">{editButton(user, name) || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </ResponsiveTable>
            )}
            <ServerPagination
              totalCount={totalCount}
              page={Math.min(page, lastPage)}
              limit={limit}
              options={LIMITS}
              onPage={(p) => updateParams({ page: p }, { keepPage: true })}
              onLimit={(l) => updateParams({ limit: l })}
            />
          </>
        )}
      </Section>
    </Box>
  );
}
